/**
 * Infinite-zoom fractals and new foreground elements.
 *
 * GPU floats run out of precision after ~10⁴× zoom. The Mandelbrot dive therefore travels: it
 * dives into a landmark, surfaces and glides to the next. The Kali zoom crossfades into a second
 * copy half a cycle behind, so it never visibly resets. The Droste spiral and nested polygons are self-similar by construction, so their loops
 * are exactly seamless.
 */
import type { VisualizationDef } from '../../../shared/viz';

const ZOOM_CROSSFADE = /* glsl */ `
// Weight of the leading copy at cycle phase f: 1 in the middle, fading out at both ends, where the
// trailing copy (half a cycle behind) is mid-dive and takes over.
float leadWeight(float f) { return smoothstep(0.0, 0.12, f) * (1.0 - smoothstep(0.88, 1.0, f)); }
mat2 rot2(float a) { float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
`;

export const mandelbrotDive: VisualizationDef = {
  id: 'mandelbrot-dive',
  name: 'Mandelbrot Dive',
  description:
    'A continuous journey through the Mandelbrot set: dive deep into a landmark, surface, glide to the next and dive again. The seed picks the route.',
  tags: ['energy:low', 'style:fractal'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Journey speed', type: 'float', min: 0.1, max: 3, default: 1 },
    { id: 'seed', label: 'Seed', type: 'float', min: 0, max: 99, default: 7, step: 1 },
    { id: 'depth', label: 'Depth', type: 'float', min: 3, max: 9, default: 8 },
    {
      id: 'detail',
      label: 'Detail',
      type: 'float',
      min: 64,
      max: 256,
      default: 128,
      step: 1,
      quality: true,
    },
    { id: 'bands', label: 'Colour bands', type: 'float', min: 0.2, max: 4, default: 1.2 },
    { id: 'spin', label: 'Spin', type: 'float', min: -1, max: 1, default: 0.1 },
  ],
  fragment: /* glsl */ `
${ZOOM_CROSSFADE}
// Landmarks on the boundary of the set, where the detail goes on forever.
vec2 landmark(float k) {
  int i = int(mod(k, 8.0));
  if (i == 0) return vec2(-0.743643887, 0.131825904);  // Seahorse Valley
  if (i == 1) return vec2(-0.775683770, 0.136467370);  // spirals off the main cardioid
  if (i == 2) return vec2(-0.101096364, 0.956286511);  // Misiurewicz spiral
  if (i == 3) return vec2(-1.754877666, 0.0);          // period-3 mini-brot on the antenna
  if (i == 4) return vec2(-0.160701350, 1.037566500);  // dendrite
  if (i == 5) return vec2(0.001643722, 0.822467633);   // spiral arms
  if (i == 6) return vec2(-1.250660000, 0.020120000);  // double spirals
  return vec2(0.285, 0.01);                             // Elephant Valley
}
// The route: the seed shuffles which landmark comes next.
vec2 stop(float k) { return landmark(floor(hash21(vec2(k, uP[1] * 17.0 + 3.0)) * 8.0)); }

void main() {
  vec2 p = rot2(uTime * 0.05 * uP[5]) * (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  // One leg per ~30 s at speed 1: dive (0–0.45), linger, surface (0.55–0.8), glide on (0.8–1).
  float t = uTime * uP[0] / 30.0;
  float k = floor(t), f = fract(t);
  float zoom = uP[2] * smoothstep(0.0, 0.45, f) * (1.0 - smoothstep(0.55, 0.8, f));
  vec2 centre = mix(stop(k), stop(k + 1.0), smoothstep(0.8, 1.0, f));
  vec2 c = centre + p * 1.6 * exp(-zoom);
  float maxIt = min(uP[3] + zoom * 24.0, 400.0);
  vec2 z = vec2(0.0);
  float n = 0.0;
  for (int i = 0; i < 400; i++) {
    if (n >= maxIt) break;
    z = vec2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + c;
    if (dot(z, z) > 256.0) break;
    n += 1.0;
  }
  vec3 col = palette(0.02) * 0.05;
  if (n < maxIt) {
    float v = (n - log2(log2(dot(z, z)))) * 0.025 * uP[4];
    col = palette(v + zoom * 0.04 + uBarPhase * 0.1) * (0.55 + 0.45 * smoothstep(0.0, 0.6, fract(v * 4.0)));
  }
  fragColor = vec4(col * (0.85 + 0.35 * pulse() + 0.2 * uBass), 1.0);
}`,
};

export const kaliZoom: VisualizationDef = {
  id: 'kali-zoom',
  name: 'Kali Zoom',
  description: 'An infinite zoom through a folding Kaliset fractal; the bass reshapes it.',
  tags: ['energy:mid', 'style:fractal'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Zoom speed', type: 'float', min: 0, max: 3, default: 1 },
    { id: 'shape', label: 'Shape', type: 'float', min: 0.3, max: 0.9, default: 0.55 },
    { id: 'glow', label: 'Glow', type: 'float', min: 0.2, max: 2, default: 1 },
    { id: 'spin', label: 'Spin', type: 'float', min: -1, max: 1, default: 0.2 },
  ],
  fragment: /* glsl */ `
${ZOOM_CROSSFADE}
vec3 kali(vec2 p, float f) {
  float depth = f * 4.6; // 100× per cycle
  vec2 z = p * 1.4 * exp(-depth) + vec2(0.52, 0.31);
  vec2 c = vec2(uP[1], uP[1] * 0.92) + 0.04 * vec2(uBass, uMid);
  float acc = 0.0, last = 0.0;
  for (int i = 0; i < 14; i++) {
    z = abs(z) / max(dot(z, z), 1e-4) - c;
    float l = length(z);
    acc += exp(-abs(l - last) * 5.0);
    last = l;
  }
  return palette(acc * 0.07 + depth * 0.12 + uBarPhase * 0.1) * clamp(acc * 0.07 * uP[2], 0.0, 1.6);
}

void main() {
  vec2 p = rot2(uTime * 0.04 * uP[3]) * (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float f = fract(uTime * 0.04 * uP[0]);
  float w = leadWeight(f);
  vec3 col = mix(kali(p, fract(f + 0.5)), kali(p, f), w);
  fragColor = vec4(col * (0.85 + 0.3 * pulse()), 1.0);
}`,
};

export const drosteSpiral: VisualizationDef = {
  id: 'droste-spiral',
  name: 'Droste Spiral',
  description: 'A picture-in-picture spiral that zooms forever; its arms are spectrum bars.',
  tags: ['energy:mid', 'style:fractal'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Zoom speed', type: 'float', min: -2, max: 2, default: 0.6 },
    { id: 'arms', label: 'Arms', type: 'float', min: 3, max: 24, default: 8, step: 1 },
    { id: 'twist', label: 'Twist', type: 'float', min: -2, max: 2, default: 0.5 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float r = length(p) + 1e-5;
  float a = atan(p.y, p.x);
  float arms = floor(uP[1]);
  // log-polar: every ring is a copy of the one inside it at twice the size
  float s = (log(r) - uTime * uP[0] * 0.3) / log(2.0);
  float ang = a / TAU * arms + s * uP[2];
  vec2 cell = vec2(fract(s), fract(ang));
  float arm = mod(floor(ang), arms);
  float ring = floor(s);
  float spec = spectrum((arm + 0.5) / arms * 0.9);
  float tile = smoothstep(0.5, 0.42, abs(cell.y - 0.5)) * smoothstep(0.0, 0.08, cell.x) * smoothstep(1.0, 0.92, cell.x);
  float lit = step(1.0 - cell.x, spec * 1.2 + 0.08);
  float edge = smoothstep(0.05, 0.0, min(cell.y, 1.0 - cell.y));
  vec3 col = palette(ring * 0.11 + arm / arms * 0.35 + uBarPhase * 0.1) * tile * (0.12 + lit * (0.7 + 0.5 * pulse()));
  col += palette(0.85 + ring * 0.11) * edge * 0.25;
  col *= smoothstep(0.0, 0.06, r);
  fragColor = vec4(col, 1.0);
}`,
};

export const nestedPolygons: VisualizationDef = {
  id: 'nested-polygons',
  name: 'Nested Polygons',
  description: 'Endless polygons flying outward, twisting; each lights with its frequency band.',
  tags: ['energy:high', 'style:geometric'],
  role: 'element',
  params: [
    { id: 'speed', label: 'Speed', type: 'float', min: -2, max: 2, default: 0.7 },
    { id: 'sides', label: 'Sides', type: 'float', min: 3, max: 8, default: 4, step: 1 },
    { id: 'twist', label: 'Twist', type: 'float', min: -1, max: 1, default: 0.25 },
    { id: 'width', label: 'Line width', type: 'float', min: 0.3, max: 3, default: 1 },
  ],
  fragment: /* glsl */ `
float polyDist(vec2 p, float n) {
  float a = atan(p.x, p.y) + PI;
  float seg = TAU / n;
  return cos(floor(0.5 + a / seg) * seg - a) * length(p);
}
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float t = uTime * uP[0] * 0.35;
  vec3 col = vec3(0.0);
  for (int k = -2; k < 10; k++) {
    // j is a polygon's identity; its size grows ×1.5 per step of t, forever.
    float j = float(k) - floor(t);
    float level = t + j;
    float size = 0.03 * pow(1.5, level);
    float fade = smoothstep(0.0, 0.05, size) * smoothstep(2.2, 1.0, size);
    if (fade <= 0.0) continue;
    float a = level * uP[2];
    float c = cos(a), s = sin(a);
    float d = abs(polyDist(mat2(c, -s, s, c) * p, floor(uP[1])) - size);
    float w = (0.002 + 0.008 * size) * uP[3] * (1.0 + 0.6 * uBass);
    float band = spectrum(fract(j * 0.137) * 0.9);
    float line = smoothstep(w, 0.0, d) + 0.25 * exp(-d / (w * 6.0));
    col += palette(j * 0.12) * line * fade * (0.35 + band * 1.4 + 0.4 * pulse());
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const beatRings: VisualizationDef = {
  id: 'beat-rings',
  name: 'Beat Rings',
  description: 'A ring is born on every beat and ripples outward; the bass thickens it.',
  tags: ['energy:high', 'style:minimal'],
  role: 'element',
  params: [
    { id: 'spacing', label: 'Spacing', type: 'float', min: 0.05, max: 0.4, default: 0.16 },
    { id: 'decay', label: 'Decay', type: 'float', min: 0.2, max: 2, default: 0.7 },
    { id: 'width', label: 'Width', type: 'float', min: 0.3, max: 3, default: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float r = length(p);
  vec3 col = vec3(0.0);
  for (int k = 0; k < 10; k++) {
    float age = float(k) + uBeatPhase; // beats since this ring was born
    float radius = age * uP[0];
    float w = (0.003 + 0.012 * uBass) * uP[2] * (1.0 + age * 0.15);
    float d = abs(r - radius);
    float strength = exp(-age * uP[1]);
    col += palette(age * 0.1 + uBarPhase * 0.2) * (smoothstep(w, 0.0, d) + 0.3 * exp(-d / (w * 5.0))) * strength;
  }
  fragColor = vec4(col * (0.7 + 0.6 * uLevel), 1.0);
}`,
};

export const waveformRibbon: VisualizationDef = {
  id: 'waveform-ribbon',
  name: 'Waveform Ribbon',
  description: 'Several glowing strands of the live waveform, braided across the screen.',
  tags: ['energy:mid', 'style:minimal'],
  role: 'element',
  params: [
    { id: 'height', label: 'Height', type: 'float', min: 0.1, max: 1.5, default: 0.7 },
    { id: 'spread', label: 'Spread', type: 'float', min: 0, max: 1, default: 0.35 },
    { id: 'strands', label: 'Strands', type: 'float', min: 1, max: 6, default: 4, step: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec3 col = vec3(0.0);
  float n = floor(uP[2]);
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    if (fi >= n) break;
    float off = (fi - (n - 1.0) * 0.5) * uP[1] * 0.06;
    float x = fract(uv.x + fi * 0.07);
    float y = 0.5 + off + waveform(x) * uP[0] * 0.35 * (1.0 - fi * 0.12);
    float d = abs(uv.y - y) * uResolution.y / uResolution.x * 1.6;
    float line = smoothstep(0.004, 0.0, d) + 0.35 * exp(-d * 70.0);
    col += palette(uv.x * 0.6 + fi * 0.12 + uBarPhase * 0.1) * line * (1.0 - fi * 0.13) * (0.8 + 0.4 * pulse());
  }
  fragColor = vec4(col, 1.0);
}`,
};

export const sparkles: VisualizationDef = {
  id: 'sparkles',
  name: 'Sparkles',
  description: 'Twinkling star flares; each listens to its own frequency and flashes on hits.',
  tags: ['energy:mid', 'style:particles'],
  role: 'element',
  params: [
    { id: 'density', label: 'Density', type: 'float', min: 8, max: 60, default: 24 },
    { id: 'amount', label: 'Amount', type: 'float', min: 0.1, max: 1, default: 0.5 },
    { id: 'flare', label: 'Flare', type: 'float', min: 0, max: 2, default: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 g = gl_FragCoord.xy / uResolution.y * uP[0];
  vec2 id = floor(g);
  vec2 f = fract(g) - 0.5;
  float h = hash21(id);
  vec2 o = (vec2(hash21(id + 3.1), hash21(id + 7.7)) - 0.5) * 0.6;
  vec2 q = f - o;
  float d = length(q);
  float tw = 0.5 + 0.5 * sin(uTime * (1.5 + h * 5.0) + h * TAU);
  float band = spectrum(h * 0.9);
  float on = step(1.0 - uP[1] * 0.7, hash21(id + 11.3));
  float b = tw * tw * (0.15 + band * 1.6 + uOnset * h * 1.5) * on;
  float core = smoothstep(0.1, 0.0, d);
  float cross = (smoothstep(0.015, 0.0, abs(q.x)) * smoothstep(0.4, 0.0, abs(q.y)) +
                 smoothstep(0.015, 0.0, abs(q.y)) * smoothstep(0.4, 0.0, abs(q.x))) * uP[2] * 0.5;
  fragColor = vec4(palette(h) * (core + cross) * b, 1.0);
}`,
};

export const drosteFrames: VisualizationDef = {
  id: 'droste-frames',
  name: 'Droste Frames',
  description:
    'Picture frames inside picture frames, zooming forever, each turned a little. Every frame’s edges are cut into frequency segments.',
  tags: ['energy:mid', 'style:fractal'],
  role: 'background',
  params: [
    { id: 'speed', label: 'Zoom speed', type: 'float', min: -2, max: 2, default: 0.5 },
    {
      id: 'segments',
      label: 'Segments per side',
      type: 'float',
      min: 2,
      max: 16,
      default: 6,
      step: 1,
    },
    { id: 'twist', label: 'Twist per frame', type: 'float', min: -0.5, max: 0.5, default: 0.12 },
    { id: 'width', label: 'Frame width', type: 'float', min: 0.1, max: 0.6, default: 0.3 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  // Each frame is the last one scaled by 1/S: log-scale coordinates make the zoom seamless.
  float S = 1.6;
  float t = uTime * uP[0] * 0.25;
  float s0 = log(max(max(abs(p.x) / 1.4, abs(p.y)), 1e-4)) / log(S) - t;
  // Twist grows smoothly with depth (no seams between frames): the classic Droste swirl.
  float ang = -(s0 + t) * uP[2];
  vec2 q = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * p;
  vec2 b = vec2(q.x / 1.4, q.y);
  float m = max(abs(b.x), abs(b.y));
  float sq = log(max(m, 1e-4)) / log(S) - t;
  float kk = floor(sq), ff = fract(sq);
  // Project onto the frame's unit square, then measure the way round it (0..1).
  vec2 e = b / max(m, 1e-4);
  float per = abs(e.y) >= abs(e.x)
    ? (e.y > 0.0 ? (e.x + 1.0) * 0.125 : 0.5 + (1.0 - e.x) * 0.125)
    : (e.x > 0.0 ? 0.25 + (1.0 - e.y) * 0.125 : 0.75 + (e.y + 1.0) * 0.125);
  float segs = floor(uP[1]) * 4.0;
  float sid = floor(per * segs);
  float band = spectrum(fract(sid / segs) * 0.9);
  float inFrame = step(1.0 - uP[3], ff);
  float gap = smoothstep(0.0, 0.06, fract(per * segs)) * smoothstep(1.0, 0.94, fract(per * segs));
  vec3 col = palette(kk * 0.13 + sid / segs * 0.5) * inFrame * gap * (0.12 + band * 1.4 + 0.25 * pulse());
  col += palette(0.9) * (1.0 - inFrame) * 0.03;
  col *= smoothstep(0.0, 0.03, m);
  fragColor = vec4(col, 1.0);
}`,
};

export const spiralArms: VisualizationDef = {
  id: 'spiral-arms',
  name: 'Spiral Arms',
  description:
    'Logarithmic spiral arms, like a galaxy, made of cells that each light with a frequency, flowing endlessly inward.',
  tags: ['energy:mid', 'style:fractal'],
  role: 'background',
  params: [
    { id: 'arms', label: 'Arms', type: 'float', min: 2, max: 12, default: 5, step: 1 },
    { id: 'pitch', label: 'Wind', type: 'float', min: 0.2, max: 2, default: 0.8 },
    { id: 'speed', label: 'Flow', type: 'float', min: -2, max: 2, default: 0.6 },
    { id: 'cells', label: 'Cells per turn', type: 'float', min: 4, max: 24, default: 12, step: 1 },
  ],
  fragment: /* glsl */ `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  float r = length(p) + 1e-5;
  float a = atan(p.y, p.x) / TAU;
  float lr = log(r);
  float arms = floor(uP[0]);
  // Along an arm: v; across arms: u. Log-radius makes the flow self-similar.
  float u = a * arms + lr * uP[1] * arms * 0.5;
  float v = lr * floor(uP[3]) * 0.4 - uTime * uP[2] * 0.5;
  // Arms counted modulo their number, so there's no seam where the angle wraps.
  float arm = mod(floor(u), arms);
  float cell = floor(v);
  vec2 f = vec2(fract(u), fract(v));
  float band = spectrum(fract(cell * 0.173 + arm * 0.29) * 0.9);
  float shape = smoothstep(0.5, 0.38, abs(f.x - 0.5)) * smoothstep(0.5, 0.36, abs(f.y - 0.5));
  vec3 col = palette(cell * 0.07 + arm / arms * 0.4) * shape * (0.1 + band * 1.5 + 0.25 * pulse());
  col += palette(0.95) * exp(-r * 14.0) * (0.5 + uBass);
  col *= smoothstep(0.0, 0.04, r);
  fragColor = vec4(col, 1.0);
}`,
};

export const FRACTAL_VISUALIZATIONS: VisualizationDef[] = [
  mandelbrotDive,
  kaliZoom,
  drosteSpiral,
  drosteFrames,
  spiralArms,
  nestedPolygons,
  beatRings,
  waveformRibbon,
  sparkles,
];
